// src/features/admin/registerAdminApi.ts
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

// Types for Admin Registration
export interface RegisterAdminData {
  full_name: string;
  email: string;
  contact_no: string;
  password: string;
  confirm_password: string;
  role: 'super_admin' | 'admin' | 'operation';
  status: number;
}

export interface RegisterAdminResponse {
  error: boolean;
  message: string;
  userId?: number;
  user_id?: number;
  data?: {
    id: number;
    full_name: string;
    email: string;
    contact_no: string;
    role: string;
    status: number;
    created_at: string;
  };
}

export interface AdminLoginData {
  email: string;
  password: string;
}

export interface AdminLoginResponse {
  error: boolean;
  message: string;
  token?: string;
  user_id?: number;
  userId?: number;
  user_name?: string;
  userName?: string;
  role?: string;
  email?: string;
}

export const ADMIN_API_CONFIG = {
  BASE_URL: process.env.REACT_APP_SERVER_ENDPOINT || 'https://fftestservice.sarsetech.com',
  AUTH_KEY: '7b4e1fd03f57885c1ff900ad76ea3810bb7160c7157a4c31cf2de3',
  ENDPOINTS: {
    REGISTER: '/admin/register',
    LOGIN: '/admin/login',
  },
};

export const registerAdminApi = createApi({
  reducerPath: 'registerAdminApi',
  baseQuery: fetchBaseQuery({
    baseUrl: ADMIN_API_CONFIG.BASE_URL,
    prepareHeaders: (headers) => {
      headers.set('Content-Type', 'application/json');
      headers.set('Accept', 'application/json');
      headers.set('authkey', ADMIN_API_CONFIG.AUTH_KEY);
      
      // Add token if available for authenticated requests
      const token = localStorage.getItem('admin_token');
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      
      return headers;
    },
  }),
  tagTypes: ['Admin'],
  endpoints: (build) => ({
    // Register Admin
    registerAdmin: build.mutation<RegisterAdminResponse, RegisterAdminData>({
      query: (data) => {
        console.log('Register Admin Data:', data);
        
        return {
          url: ADMIN_API_CONFIG.ENDPOINTS.REGISTER,
          method: 'POST',
          body: JSON.stringify({
            full_name: data.full_name.trim(),
            email: data.email.trim(),
            contact_no: data.contact_no.trim(),
            password: data.password,
            confirm_password: data.confirm_password,
            role: data.role,
            status: data.status || 1,
          }),
        };
      },
      invalidatesTags: ['Admin'],
      transformResponse: (response: any) => {
        console.log('Register Admin Response:', response);
        return {
          error: response.error || false,
          message: response.message || 'Registration successful',
          userId: response.userId || response.user_id,
          data: response.data,
        };
      },
      transformErrorResponse: (response: any) => {
        console.error('Register Admin Error:', response);
        
        // Handle specific error status codes
        if (response.status === 409) {
          return {
            status: response.status,
            data: { 
              error: true, 
              message: 'Email or contact number already exists' 
            }
          };
        } else if (response.status === 400) {
          return {
            status: response.status,
            data: { 
              error: true, 
              message: response.data?.message || 'Invalid data provided' 
            }
          };
        } else if (response.status === 403) {
          return {
            status: response.status,
            data: { 
              error: true, 
              message: 'Invalid authkey or permissions' 
            }
          };
        }
        
        return {
          status: response.status,
          data: response.data || { 
            error: true, 
            message: 'Registration failed. Please try again.' 
          }
        };
      },
    }),

    // Login Admin
    loginAdmin: build.mutation<AdminLoginResponse, AdminLoginData>({
      query: (data) => {
        console.log('Login Admin Data:', data);
        
        return {
          url: ADMIN_API_CONFIG.ENDPOINTS.LOGIN,
          method: 'POST',
          body: JSON.stringify({
            email: data.email.trim(),
            password: data.password,
          }),
        };
      },
      transformResponse: (response: any) => {
        console.log('Login Admin Response:', response);
        
        // Store admin credentials in localStorage if login successful
        if (!response.error && response.token) {
          localStorage.setItem('admin_token', response.token);
          localStorage.setItem('user_role', 'admin');
          localStorage.setItem('user_email', response.email || '');
          localStorage.setItem('admin_email', response.email || '');
        }
        
        return {
          error: response.error || false,
          message: response.message || 'Login successful',
          token: response.token,
          user_id: response.user_id || response.userId,
          user_name: response.user_name || response.userName,
          role: response.role,
          email: response.email,
        };
      },
      transformErrorResponse: (response: any) => {
        console.error('Login Admin Error:', response);
        
        // Handle specific error status codes
        if (response.status === 401) {
          return {
            status: response.status,
            data: { 
              error: true, 
              message: 'Invalid credentials. Please check your email and password.' 
            }
          };
        } else if (response.status === 403) {
          return {
            status: response.status,
            data: { 
              error: true, 
              message: 'Invalid authkey or permissions' 
            }
          };
        } else if (response.status === 404) {
          return {
            status: response.status,
            data: { 
              error: true, 
              message: 'API endpoint not found' 
            }
          };
        }
        
        return {
          status: response.status,
          data: response.data || { 
            error: true, 
            message: 'Login failed. Please try again.' 
          }
        };
      },
    }),
  }),
});

export const {
  useRegisterAdminMutation,
  useLoginAdminMutation,
} = registerAdminApi;